import { useOrgContext } from '@/org/contexts/OrgContext'
import { Visibility_Enum } from '@gql'
import {
  VisibleObjectType,
  getVisibilityNames,
} from '@rolebase/shared/model/visibility'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// The sentence that says, with the real names, who will see an object at a
// given level (CONF-02, requested by Yohan: « à chaque fois je veux expliciter
// ce que veut dire rôle, cercle ou orga en le mettant en texte »).
//
// Display only: the rule itself lives in the Hasura permissions.
export default function useVisibilitySentence(
  objectType: VisibleObjectType,
  circleId: string | undefined,
  visibility: Visibility_Enum
): string {
  const { t, i18n } = useTranslation()
  const { orgData } = useOrgContext()

  return useMemo(() => {
    if (!orgData || !circleId) return t('VisibilityField.noCircle')
    const names = getVisibilityNames(orgData, circleId)
    const list = (items: string[]) =>
      new Intl.ListFormat(i18n.language, {
        style: 'long',
        type: 'conjunction',
      }).format(items)

    const sentence = t(
      `VisibilityField.sentences.${objectType}.${visibility}`,
      {
        carrier: names.carrier,
        circles: list(names.circles),
      }
    )

    // A tension kept to its role cannot be dealt with in the meeting of the
    // circles that would otherwise read it: say which ones.
    if (
      objectType === 'thread' &&
      visibility === Visibility_Enum.Role &&
      names.others.length > 0
    ) {
      return `${sentence} ${t('VisibilityField.notInMeeting', {
        circles: list(names.others),
      })}`
    }
    return sentence
  }, [orgData, circleId, objectType, visibility, i18n.language])
}
